<template>
  <q-page class="quiz-page">
    <div class="quiz-container">
      <!-- Loading state -->
      <div v-if="loading" class="quiz-loading">
        <q-spinner-dots color="amber" size="48px" />
        <p class="loading-text">Cargando quiz...</p>
      </div>

      <!-- Empty state -->
      <div v-else-if="!loading && questions.length === 0 && state !== 'error'" class="quiz-empty">
        <div class="empty-icon">?</div>
        <p class="empty-text">No hay preguntas disponibles para este quiz.</p>
        <q-btn
          v-if="returnCourseId"
          flat
          no-caps
          label="Volver al curso"
          :to="`/learn/${returnCourseId}`"
          :style="{ color: 'var(--oa-gold-full)' }"
        />
      </div>

      <!-- Error state -->
      <div v-else-if="state === 'error'" class="quiz-empty">
        <q-icon name="error_outline" size="48px" :style="{ color: 'var(--oa-danger)' }" />
        <p class="empty-text">{{ errorMessage }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          :style="{ background: 'var(--oa-gold-full)', color: 'var(--color-marble-cream)', fontWeight: '600', borderRadius: '8px' }"
          @click="fetchQuestions"
        />
      </div>

      <!-- Results state -->
      <template v-else-if="state === 'submitted' && results">
        <!-- Score card -->
        <div class="score-card" :class="results.passed ? 'score-passed' : 'score-failed'">
          <!-- Score circle -->
          <div class="score-circle-wrapper">
            <svg class="score-svg" viewBox="0 0 112 112">
              <circle cx="56" cy="56" r="48" fill="none" stroke="rgba(0,0,0,0.05)" stroke-width="8" />
              <circle
                cx="56" cy="56" r="48" fill="none"
                :stroke="results.passed ? 'rgb(74,158,114)' : 'rgb(196,90,74)'"
                stroke-width="8"
                stroke-linecap="round"
                :stroke-dasharray="scoreCircumference"
                :stroke-dashoffset="scoreCircumference * (1 - results.scorePct / 100)"
                class="score-ring"
              />
            </svg>
            <div class="score-circle-text">
              <span
                class="score-pct"
                :class="results.passed ? 'text-passed' : 'text-failed'"
              >
                {{ results.scorePct }}%
              </span>
            </div>
          </div>

          <h2 class="score-title">{{ results.passed ? 'Aprobado!' : 'No aprobado' }}</h2>
          <p class="score-stats">
            {{ results.correctAnswers }} de {{ results.totalQuestions }} respuestas correctas
          </p>
          <span v-if="results.xpAwarded > 0" class="score-xp-badge">
            +{{ results.xpAwarded }} XP
          </span>
        </div>

        <!-- Answer review -->
        <div class="review-section">
          <h3 class="review-heading">Revision de respuestas</h3>
          <div class="review-list">
            <div
              v-for="(r, i) in results.results"
              :key="r.questionId"
              class="review-card"
            >
              <div class="review-row">
                <span
                  class="review-badge"
                  :class="r.correct ? 'badge-correct' : 'badge-wrong'"
                >
                  {{ r.correct ? '\u2713' : '\u2717' }}
                </span>
                <div class="review-body">
                  <p class="review-question">{{ i + 1 }}. {{ r.question }}</p>
                  <div class="review-options">
                    <div
                      v-for="opt in r.options"
                      :key="opt.id"
                      class="review-option"
                      :class="{
                        'opt-correct': opt.id === r.correctOptionId,
                        'opt-wrong': opt.id === r.selectedOptionId && opt.id !== r.correctOptionId,
                      }"
                    >
                      <span
                        class="opt-indicator"
                        :class="{
                          'ind-correct': opt.id === r.correctOptionId,
                          'ind-wrong': opt.id === r.selectedOptionId && opt.id !== r.correctOptionId,
                        }"
                      >
                        {{ opt.id === r.correctOptionId ? '\u2713' : (opt.id === r.selectedOptionId ? '\u2717' : '') }}
                      </span>
                      <span>{{ opt.text }}</span>
                    </div>
                  </div>
                  <p v-if="r.explanation" class="review-explanation">
                    {{ r.explanation }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="result-actions">
          <q-btn
            v-if="!results.passed"
            no-caps
            unelevated
            label="Reintentar"
            :style="{ background: 'var(--oa-gold-full)', color: 'var(--color-marble-cream)', fontWeight: '600', borderRadius: '12px', padding: '12px 24px', fontSize: '15px' }"
            @click="handleRetry"
          />
          <q-btn
            v-if="returnCourseId"
            :flat="!results.passed"
            :unelevated="results.passed"
            no-caps
            :label="results.passed ? 'Continuar' : 'Volver al curso'"
            :to="`/learn/${returnCourseId}`"
            :style="results.passed
              ? { background: 'var(--oa-gold-full)', color: 'var(--color-marble-cream)', fontWeight: '600', borderRadius: '12px', padding: '12px 24px', fontSize: '15px' }
              : { color: 'var(--oa-text-secondary)', fontSize: '15px' }"
          />
        </div>
      </template>

      <!-- Answering state -->
      <template v-else-if="state === 'answering' && currentQuestion">
        <!-- Header -->
        <div class="quiz-header">
          <p v-if="unitTitle" class="quiz-unit-title">{{ unitTitle }}</p>
          <h1 class="quiz-lesson-title">{{ lessonTitle }}</h1>

          <!-- Progress bar -->
          <div class="quiz-progress-row">
            <div class="quiz-progress-bar">
              <div
                v-for="(q, i) in questions"
                :key="q.id"
                class="progress-segment"
                :class="{
                  'segment-current': i === currentIdx,
                  'segment-answered': i !== currentIdx && answers[q.id],
                  'segment-default': i !== currentIdx && !answers[q.id],
                }"
                @click="currentIdx = i"
              />
            </div>
            <span class="quiz-progress-count">{{ answeredCount }}/{{ totalQuestions }}</span>
          </div>
        </div>

        <!-- Question card -->
        <div class="question-card">
          <div class="question-meta">
            <span class="question-number">{{ currentIdx + 1 }}</span>
            <div class="question-badges">
              <span v-if="currentQuestion.difficulty <= 2" class="diff-easy">Facil</span>
              <span v-else-if="currentQuestion.difficulty === 3" class="diff-medium">Media</span>
              <span v-else class="diff-hard">Dificil</span>
              <span class="question-xp">+{{ currentQuestion.xpReward }} XP</span>
            </div>
          </div>

          <p class="question-text">{{ currentQuestion.question }}</p>

          <div class="options-list">
            <button
              v-for="(opt, optIdx) in currentQuestion.options"
              :key="opt.id"
              class="option-btn"
              :class="{ 'option-selected': answers[currentQuestion.id] === opt.id }"
              @click="selectOption(currentQuestion.id, opt.id)"
            >
              <span
                class="option-letter"
                :class="{ 'letter-active': answers[currentQuestion.id] === opt.id }"
              >
                {{ LETTERS[optIdx] }}
              </span>
              <span
                class="option-text"
                :class="{ 'text-active': answers[currentQuestion.id] === opt.id }"
              >
                {{ opt.text }}
              </span>
            </button>
          </div>
        </div>

        <!-- Navigation -->
        <div class="quiz-nav">
          <button
            class="nav-btn nav-prev"
            :disabled="currentIdx === 0"
            @click="currentIdx = Math.max(0, currentIdx - 1)"
          >
            <span>&larr;</span>
            <span>Anterior</span>
          </button>

          <q-btn
            v-if="currentIdx < totalQuestions - 1"
            no-caps
            unelevated
            label="Siguiente"
            :disable="!answers[currentQuestion?.id]"
            :style="{ background: 'var(--oa-gold-full)', color: 'var(--color-marble-cream)', fontWeight: '600', borderRadius: '8px', padding: '8px 20px' }"
            @click="currentIdx = currentIdx + 1"
          />

          <q-btn
            v-else
            no-caps
            unelevated
            label="Enviar respuestas"
            :disable="!allAnswered"
            :loading="submitting"
            :style="{ background: allAnswered ? 'var(--oa-gold-full)' : 'var(--oa-bg-3)', color: allAnswered ? 'var(--color-marble-cream)' : 'var(--oa-text-muted)', fontWeight: '600', borderRadius: '8px', padding: '10px 24px', fontSize: '15px' }"
            @click="handleSubmit"
          />
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useQuasar } from 'quasar';
import { api } from 'boot/axios';
import { useAchievementToast } from 'src/composables/useAchievementToast';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

interface QuestionData {
  id: string;
  question: string;
  difficulty: number;
  xpReward: number;
  options: { id: string; text: string }[];
}

interface ResultOption {
  id: string;
  text: string;
  isCorrect: boolean;
}

interface QuestionResult {
  questionId: string;
  question: string;
  explanation: string | null;
  options: ResultOption[];
  selectedOptionId: string | null;
  correctOptionId: string | null;
  correct: boolean;
}

interface SubmitResults {
  attemptId: string;
  scorePct: number;
  correctAnswers: number;
  totalQuestions: number;
  passed: boolean;
  xpAwarded: number;
  results: QuestionResult[];
  newAchievements: [];
}

const $q = useQuasar();
const route = useRoute();
const { checkAndNotify } = useAchievementToast();

const lessonId = computed(() => route.params.lessonId as string);
const returnCourseId = computed(() => (route.query.courseId as string) || '');

const loading = ref(true);
const state = ref<'loading' | 'answering' | 'submitted' | 'error'>('loading');
const errorMessage = ref('');
const questions = ref<QuestionData[]>([]);
const currentIdx = ref(0);
const answers = ref<Record<string, string>>({});
const results = ref<SubmitResults | null>(null);
const submitting = ref(false);
const lessonTitle = ref('');
const unitTitle = ref('');

const scoreCircumference = 2 * Math.PI * 48;

const currentQuestion = computed(() => questions.value[currentIdx.value] || null);
const totalQuestions = computed(() => questions.value.length);
const allAnswered = computed(() => totalQuestions.value > 0 && Object.keys(answers.value).length === totalQuestions.value);
const answeredCount = computed(() => Object.keys(answers.value).length);

function selectOption(questionId: string, optionId: string) {
  answers.value = { ...answers.value, [questionId]: optionId };
}

async function fetchQuestions() {
  loading.value = true;
  state.value = 'loading';
  errorMessage.value = '';

  try {
    const { data } = await api.get('/quizzes/questions', {
      params: { lessonId: lessonId.value },
    });
    questions.value = data;
    if (data.length > 0) {
      state.value = 'answering';
    }
  } catch (err) {
    state.value = 'error';
    errorMessage.value = 'Error al cargar las preguntas';
    console.error(err);
  } finally {
    loading.value = false;
  }
}

async function handleSubmit() {
  submitting.value = true;

  try {
    const answerArray = Object.entries(answers.value).map(([questionId, selectedOptionId]) => ({
      questionId,
      selectedOptionId,
    }));

    const { data } = await api.post('/quizzes/submit', {
      lessonId: lessonId.value,
      answers: answerArray,
    });

    results.value = data;
    state.value = 'submitted';

    // Check for newly unlocked achievements on quiz pass
    if (data.passed) {
      await checkAndNotify();
    }
  } catch (err: unknown) {
    // Handle 429 cooldown
    const axiosErr = err as { response?: { status: number } };
    if (axiosErr.response?.status === 429) {
      $q.notify({
        message: 'Cooldown activo — intenta de nuevo mas tarde',
        color: 'warning',
        icon: 'schedule',
        position: 'top',
        timeout: 4000,
      });
    } else {
      $q.notify({
        message: 'Error al enviar las respuestas',
        color: 'negative',
        icon: 'error',
        position: 'top',
      });
    }
    console.error(err);
  } finally {
    submitting.value = false;
  }
}

function handleRetry() {
  answers.value = {};
  currentIdx.value = 0;
  results.value = null;
  state.value = 'answering';
}

// Try to get lesson/unit context from enrollments
async function fetchContext() {
  try {
    const { data: enrollments } = await api.get('/learn/enrollments');
    // Try to find course and unit context for this lesson
    for (const enrollment of enrollments) {
      const slug = enrollment.course?.slug;
      if (!slug) continue;

      try {
        const { data: course } = await api.get(`/courses/${slug}`);
        for (const unit of course.units || []) {
          const match = (unit.lessons || []).find(
            (l: { id: string }) => l.id === lessonId.value,
          );
          if (match) {
            lessonTitle.value = match.title || 'Quiz';
            unitTitle.value = unit.title || '';
            // If no courseId in query, set it for the "back" button
            if (!returnCourseId.value) {
              // Use a non-reactive assignment via query
            }
            return;
          }
        }
      } catch {
        // Skip this course, try next
      }
    }
  } catch {
    // Context fetch failed, proceed without titles
  }
  lessonTitle.value = 'Quiz';
}

onMounted(() => {
  fetchQuestions();
  fetchContext();
});
</script>

<style scoped lang="scss">
.quiz-page {
  padding: 24px;
}

.quiz-container {
  max-width: 700px;
  margin: 0 auto;
}

// === Loading ===
.quiz-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.loading-text {
  font-size: var(--oa-text-sm);
  color: var(--oa-text-muted);
  margin: 16px 0 0 0;
}

// === Empty ===
.quiz-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 64px 32px;
  text-align: center;
  background: var(--oa-bg-2);
  border-radius: var(--oa-radius-xl);
  border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
}

.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--oa-bg-3) 50%, transparent);
  font-size: var(--oa-text-xl);
  color: var(--oa-text-muted);
  opacity: 0.3;
  margin-bottom: 16px;
}

.empty-text {
  color: var(--oa-text-secondary);
  margin: 0;
}

// === Score card ===
.score-card {
  background: var(--oa-bg-2);
  border-radius: var(--oa-radius-xl);
  border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
  overflow: hidden;
  padding: 32px;
  text-align: center;
}

.score-passed {
  background: linear-gradient(to bottom, color-mix(in srgb, var(--oa-success) 5%, var(--oa-bg-2)), var(--oa-bg-2));
}

.score-failed {
  background: linear-gradient(to bottom, color-mix(in srgb, var(--oa-danger) 5%, var(--oa-bg-2)), var(--oa-bg-2));
}

.score-circle-wrapper {
  position: relative;
  width: 112px;
  height: 112px;
  margin: 0 auto 16px;
}

.score-svg {
  width: 112px;
  height: 112px;
  transform: rotate(-90deg);
}

.score-ring {
  transition: stroke-dashoffset 1s ease;
}

.score-circle-text {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.score-pct {
  font-size: var(--oa-text-hero);
  font-weight: 700;
}

.text-passed {
  color: var(--oa-success);
}

.text-failed {
  color: var(--oa-danger);
}

.score-title {
  font-size: var(--oa-text-lg);
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.score-stats {
  font-size: var(--oa-text-sm);
  color: var(--oa-text-secondary);
  margin: 4px 0 0 0;
}

.score-xp-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 8px;
  font-size: var(--oa-text-sm);
  font-weight: 600;
  background: color-mix(in srgb, var(--oa-gold-full) 12%, transparent);
  color: var(--oa-gold-full);
  margin-top: 12px;
}

// === Review ===
.review-section {
  margin-top: 24px;
}

.review-heading {
  font-size: var(--oa-text-xs);
  font-weight: 600;
  color: var(--oa-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 12px 4px;
}

.review-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.review-card {
  background: var(--oa-bg-2);
  border-radius: var(--oa-radius-xl);
  border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
  padding: 20px;
}

.review-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.review-badge {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 2px;
}

.badge-correct {
  background: color-mix(in srgb, var(--oa-success) 10%, transparent);
  color: var(--oa-success);
}

.badge-wrong {
  background: color-mix(in srgb, var(--oa-danger) 10%, transparent);
  color: var(--oa-danger);
}

.review-body {
  flex: 1;
  min-width: 0;
}

.review-question {
  font-weight: 500;
  font-size: var(--oa-text-sm);
  color: var(--oa-text-primary);
  margin: 0 0 12px 0;
}

.review-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.review-option {
  font-size: var(--oa-text-sm);
  padding: 10px 16px;
  border-radius: var(--oa-radius-lg);
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--oa-text-muted);
  border: 1px solid transparent;
}

.opt-correct {
  background: color-mix(in srgb, var(--oa-success) 8%, transparent);
  color: var(--oa-success);
  border-color: color-mix(in srgb, var(--oa-success) 15%, transparent);
}

.opt-wrong {
  background: color-mix(in srgb, var(--oa-danger) 8%, transparent);
  color: var(--oa-danger);
  border-color: color-mix(in srgb, var(--oa-danger) 15%, transparent);
}

.opt-indicator {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
  border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
}

.ind-correct {
  border-color: color-mix(in srgb, var(--oa-success) 30%, transparent);
  background: color-mix(in srgb, var(--oa-success) 10%, transparent);
  color: var(--oa-success);
}

.ind-wrong {
  border-color: color-mix(in srgb, var(--oa-danger) 30%, transparent);
  background: color-mix(in srgb, var(--oa-danger) 10%, transparent);
  color: var(--oa-danger);
}

.review-explanation {
  font-size: 12px;
  color: var(--oa-text-secondary);
  margin: 12px 0 0 4px;
  font-style: italic;
  line-height: 1.5;
}

// === Actions ===
.result-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
  padding-top: 8px;
}

// === Quiz header ===
.quiz-header {
  margin-bottom: 24px;
}

.quiz-unit-title {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
  margin: 0;
}

.quiz-lesson-title {
  font-size: var(--oa-text-lg);
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 4px 0 0 0;
}

.quiz-progress-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}

.quiz-progress-bar {
  flex: 1;
  display: flex;
  gap: 4px;
}

.progress-segment {
  height: 6px;
  flex: 1;
  border-radius: 3px;
  transition: background var(--oa-duration-normal) ease;
  cursor: pointer;
}

.segment-current {
  background: var(--oa-gold-full);
}

.segment-answered {
  background: color-mix(in srgb, var(--oa-gold-full) 40%, transparent);
}

.segment-default {
  background: color-mix(in srgb, var(--oa-bg-3) 50%, transparent);
}

.quiz-progress-count {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}

// === Question card ===
.question-card {
  background: var(--oa-bg-2);
  border-radius: var(--oa-radius-xl);
  border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
  padding: 24px;

  @media (min-width: 768px) {
    padding: 32px;
  }
}

.question-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
}

.question-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--oa-text-sm);
  font-weight: 700;
  background: color-mix(in srgb, var(--oa-gold-full) 10%, transparent);
  color: var(--oa-gold-full);
}

.question-badges {
  display: flex;
  align-items: center;
  gap: 8px;
}

.diff-easy {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  background: color-mix(in srgb, var(--oa-success) 12%, transparent);
  color: var(--oa-success);
}

.diff-medium {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  background: color-mix(in srgb, var(--oa-gold-full) 12%, transparent);
  color: var(--oa-gold-full);
}

.diff-hard {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  background: color-mix(in srgb, var(--oa-danger) 12%, transparent);
  color: var(--oa-danger);
}

.question-xp {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
}

.question-text {
  font-size: var(--oa-text-md);
  font-weight: 500;
  color: var(--oa-text-primary);
  line-height: 1.5;
  margin: 0 0 24px 0;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option-btn {
  width: 100%;
  text-align: left;
  padding: 16px 20px;
  border-radius: var(--oa-radius-lg);
  font-size: var(--oa-text-sm);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border: 2px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
  background: transparent;
  cursor: pointer;
  transition: all var(--oa-duration-fast) ease;

  &:hover {
    border-color: color-mix(in srgb, var(--oa-gold-full) 30%, transparent);
    background: color-mix(in srgb, var(--oa-gold-full) 3%, transparent);
  }
}

.option-selected {
  border-color: var(--oa-gold-full) !important;
  background: color-mix(in srgb, var(--oa-gold-full) 5%, transparent) !important;
  box-shadow: 0 2px 8px rgba(232, 184, 75, 0.1);
}

.option-letter {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  background: color-mix(in srgb, var(--oa-bg-3) 50%, transparent);
  color: var(--oa-text-muted);
  transition: all var(--oa-duration-fast) ease;
}

.letter-active {
  background: var(--oa-gold-full) !important;
  color: var(--color-marble-cream) !important;
}

.option-text {
  padding-top: 4px;
  color: var(--oa-text-secondary);
  transition: color var(--oa-duration-fast) ease;
}

.text-active {
  color: var(--oa-text-primary) !important;
  font-weight: 500;
}

// === Quiz nav ===
.quiz-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 24px;
}

.nav-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: var(--oa-radius-lg);
  font-size: var(--oa-text-sm);
  color: var(--oa-text-secondary);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all var(--oa-duration-fast) ease;

  &:hover:not(:disabled) {
    background: color-mix(in srgb, var(--oa-bg-3) 30%, transparent);
    color: var(--oa-text-primary);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}
</style>
